import { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import SEO from '@/components/seo/SEO';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trophy, ArrowLeft, Store } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SITE = 'https://getbitsy.ai';

export default function VenuesCityPage() {
  const { citySlug } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/venue-partners/leaderboard/city/${citySlug}`);
        setData(r.data);
        setStatus('ok');
      } catch (e) {
        setStatus(e?.response?.status === 404 ? '404' : 'error');
      }
    })();
  }, [citySlug]);

  if (status === '404') return <Navigate to="/venues" replace />;

  if (status !== 'ok') {
    return (
      <div className="min-h-screen bg-[#0a0e1a] text-zinc-100 flex items-center justify-center">
        <p className="text-zinc-500 text-sm">{status === 'loading' ? 'Loading…' : 'Something went wrong.'}</p>
      </div>
    );
  }

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `Top BHERMES venues in ${data.city}, ${data.country}`,
    numberOfItems: data.venues.length,
    itemListElement: data.venues.slice(0, 20).map((v, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: v.name,
    })),
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-zinc-100" data-testid="venues-city-page">
      <SEO
        title={`Top venues in ${data.city} using BHERMES verification`}
        description={`${data.venue_count} venue${data.venue_count === 1 ? '' : 's'} in ${data.city}, ${data.country} running the BHERMES Trusted Guest scanner. ${data.total_scans} total scans. Public leaderboard — zero PII.`}
        canonical={`${SITE}/venues/${data.slug}`}
        schema={itemListSchema}
      />

      <header className="border-b border-zinc-800/60 px-6 py-4 flex justify-between items-center">
        <Link to="/venues" className="text-sm tracking-widest text-zinc-400 hover:text-zinc-200">
          <ArrowLeft className="inline w-3 h-3 mr-1" /> All cities
        </Link>
        <Link to="/verify-badge/venue" className="text-xs tracking-widest text-amber-400 hover:text-amber-300">
          + Join in {data.city}
        </Link>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16 space-y-10">
        <section>
          <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 mb-4">CITY LEADERBOARD</Badge>
          <h1 className="text-5xl font-semibold leading-tight">
            <span className="text-amber-400">{data.city}</span>'s top doors on BHERMES.
          </h1>
          <p className="text-zinc-400 mt-4 max-w-2xl">
            {data.venue_count} venue{data.venue_count === 1 ? '' : 's'} in {data.city}, {data.country} ·
            {' '}{data.total_scans} total scans this period.
          </p>
        </section>

        {data.venues.length === 0 ? (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-10 text-center space-y-3">
              <Store className="w-10 h-10 text-zinc-500 mx-auto" />
              <p className="text-zinc-300">No scans logged yet in {data.city}.</p>
              <Link to="/verify-badge/venue" className="text-amber-400 hover:underline text-sm">Be the first venue →</Link>
            </CardContent>
          </Card>
        ) : (
          <section className="space-y-2" data-testid="city-leaderboard-list">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-400" /> Top scanners
            </h2>
            {data.venues.map((v, i) => (
              <div key={v.venue_id}
                className="flex items-center gap-4 p-4 rounded-lg border border-zinc-800 bg-zinc-900/40"
                data-testid={`city-venue-${v.venue_id}`}>
                <span className={`text-2xl font-semibold w-8 ${i < 3 ? 'text-amber-400' : 'text-zinc-500'}`}>#{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-zinc-100 truncate">{v.name}</p>
                  <p className="text-xs text-zinc-500">{v.type}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-zinc-100 font-mono">{v.distinct_guests}</p>
                  <p className="text-[10px] text-zinc-500 tracking-widest">GUESTS</p>
                </div>
              </div>
            ))}
          </section>
        )}
      </main>

      <footer className="border-t border-zinc-800/60 text-center py-6 text-[11px] text-zinc-600 tracking-wider">
        Powered by BHERMES · M.O.A.T. · <Link to="/" className="hover:text-zinc-400">getbitsy.ai</Link>
      </footer>
    </div>
  );
}
